/**
 * Saved Queries API Service
 *
 * Handles all API calls related to saved/bookmarked queries.
 */

import {
  SavedQuery,
  SaveQueryResponse,
  SearchSavedQueriesResponse,
} from '../types/savedQueries';
import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

export async function saveQuery(
  threadId: string,
  workflowId: string,
  spaceId: string,
  tags: string[] = []
): Promise<SaveQueryResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/threads/${threadId}/workflows/${workflowId}/save`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ thread_id: threadId, workflow_id: workflowId, space_id: spaceId, tags }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save query' }));
    throw new Error(error.detail || 'Failed to save query');
  }
  return response.json();
}

export async function searchSavedQueries(query: string, limit: number = 5): Promise<SavedQuery[]> {
  const params = new URLSearchParams({ q: query, limit: limit.toString() });
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/saved-queries/search?${params}`),
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to search queries' }));
    throw new Error(error.detail || 'Failed to search queries');
  }
  const data: SearchSavedQueriesResponse = await response.json();
  return data.saved_queries;
}

export async function listSavedQueries(limit: number = 20): Promise<SavedQuery[]> {
  const params = new URLSearchParams({ limit: limit.toString() });
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/saved-queries?${params}`),
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to list queries' }));
    throw new Error(error.detail || 'Failed to list queries');
  }
  const data: SearchSavedQueriesResponse = await response.json();
  return data.saved_queries;
}

export async function deleteSavedQuery(savedQueryId: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/saved-queries/${savedQueryId}`),
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete query' }));
    throw new Error(error.detail || 'Failed to delete query');
  }
}

export async function replaySavedQuery(
  savedQueryId: string,
  newThreadId?: string,
  clientMsgId?: string
): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/saved-queries/${savedQueryId}/replay`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ new_thread_id: newThreadId, client_msg_id: clientMsgId }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to replay query' }));
    throw new Error(error.detail || 'Failed to replay query');
  }
  if (!response.body) throw new Error('No response body for replay stream');
  return response.body;
}
